import type { FocusEvent } from 'react';
import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { SearchTextBox } from '../../../../components/SearchTextBox/SearchTextBox';
import { StyledFieldLabel } from '../styles';
import type { Recipient } from '../types';
import { useRecipientContacts } from '../useRecipientContacts';

import {
  StyledChipInfo,
  StyledChipName,
  StyledChipRemove,
  StyledChipRole,
  StyledDropdown,
  StyledDropdownHeader,
  StyledDropdownItem,
  StyledDropdownName,
  StyledDropdownRole,
  StyledRecipientChip,
  StyledRecipientSection,
  StyledSearchWrapper,
} from './styles';

type AIOutreachRecipientDropdownProps = {
  domain?: string;
  isDisabled?: boolean;
  selectedRecipient: Recipient | null;
  onSelectRecipient: (recipient: Recipient | null) => void;
};

export const AIOutreachRecipientDropdown = ({
  domain,
  isDisabled = false,
  selectedRecipient,
  onSelectRecipient,
}: AIOutreachRecipientDropdownProps) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const { data: contacts = [], isFetching } = useRecipientContacts(domain, searchText, isDropdownOpen);

  const handleSearchChange = useCallback((text: string) => {
    setSearchText(text);
  }, []);

  const handleSearchClear = () => {
    setSearchText('');
  };

  const handleFocus = () => {
    setIsDropdownOpen(true);
  };

  const handleBlur = (e: FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDropdownOpen(false);
    }
  };

  const handleSelect = useCallback(
    (name: string, role: string) => {
      onSelectRecipient({ name, role });
      setSearchText('');
      setIsDropdownOpen(false);
    },
    [onSelectRecipient]
  );

  const handleRemove = useCallback(() => {
    onSelectRecipient(null);
    setSearchText('');
  }, [onSelectRecipient]);

  return (
    <StyledRecipientSection data-automation="AIOutreachRecipientField">
      <StyledFieldLabel>{t('company.overview.ai-outreach.recipient', 'Recipient (optional)')}</StyledFieldLabel>
      {selectedRecipient ? (
        <StyledRecipientChip>
          <StyledChipInfo>
            <StyledChipName>{selectedRecipient.name}</StyledChipName>
            <StyledChipRole>{selectedRecipient.role}</StyledChipRole>
          </StyledChipInfo>
          <StyledChipRemove onClick={handleRemove} disabled={isDisabled}>
            <SWReactIcons iconName="close" size="xs" iconColor={colorsPalettes.carbon[500]} />
          </StyledChipRemove>
        </StyledRecipientChip>
      ) : (
        <StyledSearchWrapper onFocus={handleFocus} onBlur={handleBlur}>
          <SearchTextBox
            searchText={searchText}
            placeholder={String(t('company.overview.ai-outreach.search_placeholder', 'Search by name or job title'))}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            isLoading={isFetching}
            isDisabled={isDisabled}
            autoFocus={false}
            debounceTime={300}
          />
          {isDropdownOpen && contacts.length > 0 ? (
            <StyledDropdown isVisible>
              <StyledDropdownHeader>
                {t('company.overview.ai-outreach.suggested_contacts', 'Suggested contacts')}
              </StyledDropdownHeader>
              {contacts.map((contact) => (
                <StyledDropdownItem
                  key={contact.id}
                  onMouseDown={() => handleSelect(contact.name, contact.title)}
                  data-automation="AIOutreachDropdownItem"
                >
                  <StyledDropdownName>{contact.name}</StyledDropdownName>
                  <StyledDropdownRole>{contact.title}</StyledDropdownRole>
                </StyledDropdownItem>
              ))}
            </StyledDropdown>
          ) : null}
        </StyledSearchWrapper>
      )}
    </StyledRecipientSection>
  );
};
